import { ContentPaste, SyncProblem } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { Fragment, useCallback, useRef, useState } from "react";
import ContextMenuDropdown, {
  type ContextMenuRef,
} from "../../../../components/shared/ContextMenuDropdown";
import Loading from "../../../../components/shared/layout/Loading";
import { CollapsibleItem } from "../../../../components/shared/sidebar/CollapsibleItem";
import { useSelectionContext } from "../../../../context/SelectionContext";
import { useOSADShipments } from "../../../../hooks/react-query/operations/useOSAD";
import { useDateSearchParamOrFallbackToToday } from "../../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { osadDefaultValue } from "../../../../mocks/operations/osad.mock";
import type { ContextMenuAction } from "../../../../types/contextMenuAction";
import { dateToInt } from "../../../../utils/dateTimeHelper";
import { OSADShipmentItem } from "../../inboundPlanning/OSADShipmentItem";
import { osadContextMenuActions } from "../../inboundPlanning/tables/osadContextMenuActions";
import {
  type SummaryContext,
  useActionsMenuContext,
} from "../ActionsMenuContext";
import { NoResultFound } from "./SectionItem";

export const OSAD = () => {
  const contextMenuRef = useRef<ContextMenuRef<SummaryContext>>(null);

  const { clearSelection } = useSelectionContext();
  const { openDialog } = useActionsMenuContext();
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: osadShipments = osadDefaultValue, isLoading } =
    useOSADShipments(serviceCenter.id);

  const contextActions = useCallback(
    (context?: SummaryContext): ContextMenuAction[] =>
      osadContextMenuActions({
        serviceCenter,
        date: planDate,
        osad: osadShipments,
        shipmentContext: context ? [context] : [],
        openDialog,
        clearSelection,
      }),
    [clearSelection, openDialog, osadShipments, planDate, serviceCenter]
  );

  if (isLoading) return <Loading />;

  return (
    <>
      <CollapsibleItem
        title="OSAD"
        displayCount={osadShipments.summary.bills}
        icon={<SyncProblem />}
        isExpanded={isExpanded}
        onExpand={(value: boolean) => setIsExpanded(value)}
      >
        <>
          {osadShipments.shipments.map((shipment, index) => (
            <Fragment key={shipment.shipmentId}>
              <OSADShipmentItem
                shipment={shipment}
                index={index}
                contextRef={contextMenuRef}
              />
            </Fragment>
          ))}
          {osadShipments.shipments.length === 0 && (
            <NoResultFound>
              <ContentPaste sx={{ fontSize: "2rem" }} />
              <Typography>No OSAD shipments found.</Typography>
            </NoResultFound>
          )}
        </>
      </CollapsibleItem>
      <ContextMenuDropdown actions={contextActions} ref={contextMenuRef} />
    </>
  );
};
